import {z} from 'zod';

// ============================================================================
// Firebase Role Types
// ============================================================================

export const ALL_FIREBASE_ROLES = [
  'student',
  'sl',
  'seniorsl',
  'ta',
  'admin',
  'instructor',
  'mentor',
  'experiencedstudent',
  'unregistered',
] as const;

export type RoleFirebaseString = (typeof ALL_FIREBASE_ROLES)[number];

export const RoleFirebaseStringSchema = z.enum(ALL_FIREBASE_ROLES);

/**
 * The roles that make someone *internal course staff*, as the Firestore role
 * strings written to `users/{uid}/roles/{courseId}`. Mirrors the
 * `isAdmin() || isTA() || isInstructor()` arm of `isStaff()` in
 * `firestore.rules`; `superadmin` has no role string because it is global
 * rather than per-course.
 *
 * Deliberately excludes `sl`, `seniorsl`, and `mentor`: those are volunteer
 * teachers who legitimately go through the section-leader application, so
 * treating them as staff would drop real applicants from the funnel.
 *
 * Used to keep staff test accounts out of application counts — see
 * `getCourseStaffUids`.
 */
export const STAFF_ROLE_FIREBASE_STRINGS = [
  'admin',
  'instructor',
  'ta',
] as const satisfies readonly RoleFirebaseString[];

/*
 * Right now we have a straight-forward permissions model: admins have
 * strictly more access than section leaders, who have strictly more
 * access than students. We can gate content by marking the least restrictive
 * user group that has access to it (e.g. SECTION_LEADER), which will
 * ensure any groups with more permissions (e.g. ADMIN) will also have
 * access to it.
 *
 * NOTE: the numeric values are NOT the effective permission order. The
 * canonical hierarchy is {@link ROLE_PRIORITY} below (used by
 * {@link isMinimumRole}), mirrored by hand in `firestore.rules` /
 * `storage.rules` (`rolePriorityMap`). Keep those ladders in sync.
 */
export enum Role {
  UNREGISTERED = 0,
  STUDENT = 1,
  SECTION_LEADER = 2,
  TA = 3,
  ADMIN = 4,
  INSTRUCTOR = 5,
  EXPERIENCED_STUDENT = 6,
  MENTOR = 7,
  // Senior section leaders: veteran SLs with every SL permission plus the
  // ability to review SL applications. Ranked just above SECTION_LEADER and
  // below TA in the ROLE_PRIORITY ladder below.
  SENIOR_SL = 8,
}

// ============================================================================
// Role display strings
// ============================================================================

/**
 * Human-readable role label used in general user-facing UI.
 */
export type RoleDisplayString =
  | 'Unregistered'
  | 'Student'
  | 'Section Leader'
  | 'Senior Section Leader'
  | 'TA'
  | 'Admin'
  | 'Instructor'
  | 'Experienced Student'
  | 'Section Leader (Mentor)'
  | 'Unknown';

/**
 * Short role badge shown beside a user's name in the forum. The empty string
 * denotes "no badge shown" (e.g. students).
 */
export type RoleForumString =
  | 'SL'
  | 'Sr. SL'
  | 'Mentor'
  | 'TA'
  | 'Instructor'
  | 'Admin'
  | '';

/**
 * Role label shown in admin-facing surfaces (user management, rosters, etc.).
 */
export type RoleAdminString =
  | 'Unregistered'
  | 'Student'
  | 'Section Leader'
  | 'Senior Section Leader'
  | 'TA'
  | 'Instructor'
  | 'Admin'
  | 'Section Leader (Mentor)'
  | 'Experienced Student'
  | 'Unknown';

// ============================================================================
// Type Guard
// ============================================================================

/**
 * Type guard for RoleFirebaseString.
 * Uses Zod schema internally for validation.
 */
export function isFirebaseRole(role: unknown): role is RoleFirebaseString {
  return RoleFirebaseStringSchema.safeParse(role).success;
}

// ============================================================================
// Role hierarchy
// ============================================================================

/**
 * Effective permission ordering, lowest-privileged first. This is the canonical
 * hierarchy — it is intentionally NOT the numeric order of the {@link Role}
 * enum (e.g. MENTOR ranks below SECTION_LEADER here). Mirrored, and kept in
 * sync by hand, in `firestore.rules` and `storage.rules` (`rolePriorityMap`).
 */
const ROLE_PRIORITY: readonly Role[] = [
  Role.UNREGISTERED,
  Role.STUDENT,
  Role.EXPERIENCED_STUDENT,
  Role.MENTOR,
  Role.SECTION_LEADER,
  Role.SENIOR_SL,
  Role.TA,
  Role.ADMIN,
  Role.INSTRUCTOR,
];

/**
 * Returns whether `courseRole` is ranked at least as high as `requiredRole` in
 * the {@link ROLE_PRIORITY} ladder. The comparison is inclusive of the
 * boundary: `isMinimumRole(r, r)` is always `true`.
 *
 * @param courseRole - The role the caller actually holds.
 * @param requiredRole - The minimum role the gated action requires.
 * @returns `true` iff `courseRole` ranks >= `requiredRole`. Returns `false` if
 *   either role is absent from the ladder.
 */
export function isMinimumRole(courseRole: Role, requiredRole: Role): boolean {
  const courseRoleIndex = ROLE_PRIORITY.indexOf(courseRole);
  const requiredRoleIndex = ROLE_PRIORITY.indexOf(requiredRole);

  if (courseRoleIndex === -1 || requiredRoleIndex === -1) {
    return false;
  }

  return courseRoleIndex >= requiredRoleIndex;
}

/**
 * Returns whether `courseRole` ranks strictly above `otherRole` in the
 * {@link ROLE_PRIORITY} ladder. The comparison is exclusive: a role never
 * outranks itself.
 *
 * @param courseRole - The role the caller actually holds.
 * @param otherRole - The role being compared against.
 * @returns `true` iff `courseRole` ranks > `otherRole`. Returns `false` if
 *   either role is absent from the ladder.
 */
export function isHigherRole(courseRole: Role, otherRole: Role): boolean {
  return courseRole !== otherRole && isMinimumRole(courseRole, otherRole);
}

// ============================================================================
// Role conversions
// ============================================================================

/**
 * Converts a {@link Role} enum value to its Firestore string representation.
 *
 * @param courseRole - The role enum value to convert.
 * @returns The canonical Firestore role string; `'unregistered'` for any
 *   unrecognized value.
 */
export function roleToFirebaseString(courseRole: Role): RoleFirebaseString {
  switch (courseRole) {
    case Role.UNREGISTERED:
      return 'unregistered';
    case Role.STUDENT:
      return 'student';
    case Role.SECTION_LEADER:
      return 'sl';
    case Role.SENIOR_SL:
      return 'seniorsl';
    case Role.ADMIN:
      return 'admin';
    case Role.TA:
      return 'ta';
    case Role.INSTRUCTOR:
      return 'instructor';
    case Role.MENTOR:
      return 'mentor';
    case Role.EXPERIENCED_STUDENT:
      return 'experiencedstudent';
    default:
      return 'unregistered';
  }
}

/**
 * Converts a Firestore role string to its {@link Role} enum value.
 *
 * @param firebaseStr - The Firestore role string to convert.
 * @returns The matching role enum value; {@link Role.UNREGISTERED} for any
 *   unrecognized string.
 */
export function firebaseStringToRole(firebaseStr: RoleFirebaseString): Role {
  switch (firebaseStr) {
    case 'student':
      return Role.STUDENT;
    case 'sl':
      return Role.SECTION_LEADER;
    case 'seniorsl':
      return Role.SENIOR_SL;
    case 'admin':
      return Role.ADMIN;
    case 'ta':
      return Role.TA;
    case 'instructor':
      return Role.INSTRUCTOR;
    case 'mentor':
      return Role.MENTOR;
    case 'experiencedstudent':
      return Role.EXPERIENCED_STUDENT;
    default:
      return Role.UNREGISTERED;
  }
}

/**
 * Converts a {@link Role} to its human-readable label for general UI.
 *
 * @param courseRole - The role enum value to convert.
 * @returns The display label; `'Unknown'` for any unrecognized value.
 */
export function roleToDisplayString(courseRole: Role): RoleDisplayString {
  switch (courseRole) {
    case Role.UNREGISTERED:
      return 'Unregistered';
    case Role.STUDENT:
      return 'Student';
    case Role.SECTION_LEADER:
      return 'Section Leader';
    case Role.SENIOR_SL:
      return 'Senior Section Leader';
    case Role.TA:
      return 'TA';
    case Role.INSTRUCTOR:
      return 'Instructor';
    case Role.ADMIN:
      return 'Admin';
    case Role.MENTOR:
      return 'Section Leader (Mentor)';
    case Role.EXPERIENCED_STUDENT:
      return 'Experienced Student';
    default:
      return 'Unknown';
  }
}

/**
 * Converts a {@link Role} to its short forum badge string.
 *
 * @param courseRole - The role enum value to convert.
 * @returns The forum badge; the empty string for roles that show no badge.
 */
export function roleToForumString(courseRole: Role): RoleForumString {
  switch (courseRole) {
    case Role.SECTION_LEADER:
      return 'SL';
    case Role.SENIOR_SL:
      return 'Sr. SL';
    case Role.TA:
      return 'TA';
    case Role.INSTRUCTOR:
      return 'Instructor';
    case Role.ADMIN:
      return 'Admin';
    case Role.MENTOR:
      return 'Mentor';
    default:
      return '';
  }
}

/**
 * Converts a {@link Role} to its label for admin-facing surfaces.
 *
 * @param courseRole - The role enum value to convert.
 * @returns The admin label; `'Unknown'` for any unrecognized value.
 */
export function roleToAdminString(courseRole: Role): RoleAdminString {
  switch (courseRole) {
    case Role.UNREGISTERED:
      return 'Unregistered';
    case Role.STUDENT:
      return 'Student';
    case Role.SECTION_LEADER:
      return 'Section Leader';
    case Role.SENIOR_SL:
      return 'Senior Section Leader';
    case Role.TA:
      return 'TA';
    case Role.INSTRUCTOR:
      return 'Instructor';
    case Role.ADMIN:
      return 'Admin';
    case Role.MENTOR:
      return 'Section Leader (Mentor)';
    case Role.EXPERIENCED_STUDENT:
      return 'Experienced Student';
    default:
      return 'Unknown';
  }
}
